import { Blac, Cubit } from "blac-next";
import PushNotificationsBloc from "src/hybrid/components/PushNotificationsControls";
import createTrackEvent from "src/lib/createTrackEvent";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import StorageBloc, {
  StorageController
} from "src/state/StorageBloc/StorageBloc";
import { tracker } from "src/state/state";
import ConversationThreadCubit from "src/ui/components/LeapChat/state/ConversationThreadCubit";

const DISMISS_STORAGE_KEY = StorageBloc.keys.pushPromptDismissedAt;
const DISMISS_COOLDOWN_MS = 5 * 24 * 60 * 60 * 1000;

export interface RequestPushPermissionsState {
  show: boolean;
}

export default class RequestPushPermissionsCubit extends Cubit<RequestPushPermissionsState> {
  static keepAlive = true;

  constructor() {
    super({ show: false });
  }

  init = (): void => {
    this.hidePrompt();
    const appView = Blac.getBloc(AppViewCubit);
    appView.setOnEscalationOccurredCallback(() => this.evaluate());
  };

  private readonly logEnabled = false;
  log = (
    label: string,
    reason: string,
    details?: Record<string, unknown>
  ): void => {
    if (this.logEnabled) {
      console.info(label, reason, details ?? {});
    }
  };

  evaluate = (): void => {
    const pushBloc = Blac.getBloc(PushNotificationsBloc);
    const appView = Blac.getBloc(AppViewCubit);
    const { escalationOccurred = false } = appView.state;

    const thread =
      Blac.getAllBlocs(ConversationThreadCubit).find(
        (t) => !!t.conversationId
      ) ?? null;
    const messages = thread?.state.messages ?? [];
    const messagesLength = messages.length;
    const lastMessage = messages[messages.length - 1];
    const lastMessageIsFromUser = lastMessage?.role === "user";

    const log = (reason: string) =>
      this.log("[PushPrompt]", reason, {
        isAvailable: pushBloc.isAvailable,
        permission: pushBloc.state.permissions?.receive,
        messagesLength,
        lastMessageIsFromUser,
        escalationOccurred,
        dismissedRecently: this.isDismissedRecently(),
        isInitializing: thread?.state.isInitializing
      });

    // Check validation conditions
    const skipReason = this.getSkipReason(
      pushBloc,
      thread,
      messagesLength,
      lastMessageIsFromUser,
      escalationOccurred
    );

    if (skipReason) {
      log(skipReason);
      this.hidePrompt();
      return;
    }

    log("show: showing prompt");
    this.patch({ show: true });
    if (escalationOccurred) {
      appView.setEscalationOccurred(false);
    }
    this.trackInitiated();
  };

  private getSkipReason(
    pushBloc: PushNotificationsBloc,
    thread: ConversationThreadCubit | null,
    messagesLength: number,
    lastMessageIsFromUser: boolean,
    escalationOccurred: boolean
  ): string | null {
    if (!pushBloc.isAvailable) {
      return "skip: push not available";
    }
    if (pushBloc.state.permissions?.receive === "granted") {
      return "skip: permission already granted";
    }
    if (!thread?.conversationId || thread.state.isInitializing) {
      return "skip: thread not ready";
    }
    if (messagesLength === 0) {
      return "skip: no messages";
    }
    if (this.isDismissedRecently()) {
      return "skip: dismissed recently";
    }

    const shouldTrigger =
      lastMessageIsFromUser || escalationOccurred || this.state.show;
    if (!shouldTrigger) {
      return "skip: no trigger (need lastMessageIsFromUser or escalationOccurred)";
    }

    return null;
  }

  handlePromptNow = (): void => {
    tracker.track(createTrackEvent("Push Notification Access - Prompt"));
    const pushBloc = Blac.getBloc(PushNotificationsBloc);
    void pushBloc.requestPermissions({ goToSettings: true });
  };

  handleDismiss = (): void => {
    this.hidePrompt();
    StorageController.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
  };

  private hidePrompt(): void {
    if (this.state.show) {
      this.patch({ show: false });
    }
  }

  private isDismissedRecently(): boolean {
    const raw = StorageController.getItem(DISMISS_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const dismissedAt = parseInt(raw, 10);
    if (Number.isNaN(dismissedAt)) {
      return false;
    }
    return Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
  }

  private trackInitiated(): void {
    tracker.track(createTrackEvent("Push Notification Access - Initiated"));
  }
}
